/**
 * Credits Service — frontend client for the metered-billing credit ledger.
 *
 * Backend contracts (Wave 2, Agent D):
 *   GET  /credits/balance          → { balanceMilliCents, balanceUsd, recent: [...] }
 *   POST /credits/checkout         body: { amountUsd }   → { url, sessionId }
 *
 * `balanceMilliCents` is an integer where $1 = 100_000 milli-cents (4dp
 * precision). Always do display rounding from the milli-cents value so
 * floating-point error doesn't drift across operations.
 */
import { accountApi as apiService } from './api';

/**
 * Custom event dispatched whenever a credit-affecting action occurs
 * (purchase, deploy, run completion). Listeners (e.g. CreditBalance
 * header chip) refetch the balance on receipt.
 *
 * Use:
 *   window.dispatchEvent(new CustomEvent(CREDIT_CHANGED_EVENT));
 *   window.addEventListener(CREDIT_CHANGED_EVENT, handler);
 */
export const CREDIT_CHANGED_EVENT = 'credit-changed';

/**
 * Format a milli-cents integer for display. By default returns the
 * 2-decimal USD string ($12.34). Pass `{ precision: 4 }` for the full
 * 4-dp form ($12.3456) used in the tooltip.
 *
 * Note: milli-cents = USD * 100_000. Negative values are formatted with
 * a leading minus inside the dollar sign ($-1.23 is ugly so we render
 * -$1.23 instead).
 *
 * @param {number|string|null|undefined} milliCents
 * @param {{ precision?: number }} [opts]
 * @returns {string}
 */
export function formatBalance(milliCents, { precision = 2 } = {}) {
  const mc = Number(milliCents);
  if (!Number.isFinite(mc)) return '$0.00';
  const usd = mc / 100000;
  const abs = Math.abs(usd).toFixed(precision);
  return usd < 0 ? `-$${abs}` : `$${abs}`;
}

/**
 * Format a milli-cents-per-minute rate as a per-hour USD string.
 *
 * Rate-card entries use mc/min granularity (lowest practical unit) but
 * humans price compute in $/hr. 1 mc/min × 60 = 60 mc/hr → /100_000 USD.
 *
 * @param {number} milliCentsPerMin
 * @returns {string}  e.g. "$0.12/hr"
 */
export function formatRate(milliCentsPerMin) {
  const mc = Number(milliCentsPerMin);
  if (!Number.isFinite(mc) || mc <= 0) return '$0.00/hr';
  const usdPerHour = (mc * 60) / 100000;
  // Below 1¢/hr we want 4 decimal places (e.g. $0.0030/hr); otherwise 2
  // decimals reads cleaner ($0.05/hr, $0.12/hr).
  const fixed = usdPerHour < 0.01 ? usdPerHour.toFixed(4) : usdPerHour.toFixed(2);
  return `$${fixed}/hr`;
}

/**
 * Format a duration in milli-cents-per-minute against a USD balance,
 * returning approximate hours-of-runtime. Used by the deploy modal:
 *   "≈ 247 hours of runtime"
 */
export function hoursOfRuntime(milliCentsPerMin, balanceMilliCents) {
  const ratePerMin = Number(milliCentsPerMin);
  const bal = Number(balanceMilliCents);
  if (!Number.isFinite(ratePerMin) || ratePerMin <= 0) return null;
  if (!Number.isFinite(bal) || bal <= 0) return 0;
  const minutes = bal / ratePerMin;
  return minutes / 60;
}

// Fallback limits used if the network request to /credits/limits fails
// before the modal renders (rare; never block the UI on a transient 5xx).
// Match the backend DEFAULT_CREDIT_LIMITS in pricing-rates.js so the UX
// is consistent even with the fallback in play.
const FALLBACK_LIMITS = {
  minTopupUsd: 25,
  maxTopupUsd: 10000,
  quickAmountsUsd: [25, 50, 100, 250],
};

const Credits = {
  async getBalance() {
    return apiService.request('/credits/balance');
  },

  /**
   * Fetch the live top-up bounds + quick-pick amounts. Backend resolves
   * per-account overrides (creditLimitsOverride) so the UI honors VIP
   * accounts without a frontend change. Falls back to FALLBACK_LIMITS on
   * network error so the modal still renders.
   */
  async getLimits() {
    try {
      const res = await apiService.request('/credits/limits');
      // Defensive: backend may return partial shape if config edits land
      // before a redeploy. Merge over fallback so missing keys still work.
      return {
        minTopupUsd: Number(res?.minTopupUsd) || FALLBACK_LIMITS.minTopupUsd,
        maxTopupUsd: Number(res?.maxTopupUsd) || FALLBACK_LIMITS.maxTopupUsd,
        quickAmountsUsd: Array.isArray(res?.quickAmountsUsd) && res.quickAmountsUsd.length
          ? res.quickAmountsUsd
          : FALLBACK_LIMITS.quickAmountsUsd,
      };
    } catch (e) {
      return { ...FALLBACK_LIMITS };
    }
  },

  /**
   * Kick off a Stripe Checkout session for a credit top-up. Bounds are
   * RE-validated server-side (against per-account creditLimitsOverride),
   * so the client-side check here is purely a fast-path / UX guard.
   * @param {number} amountUsd  whole-dollar amount
   * @param {{ minTopupUsd?: number, maxTopupUsd?: number }} [limits]
   * @returns {Promise<{ url: string, sessionId: string }>}
   */
  async createCheckout(amountUsd, limits) {
    const amt = Number(amountUsd);
    const min = Number(limits?.minTopupUsd) || FALLBACK_LIMITS.minTopupUsd;
    const max = Number(limits?.maxTopupUsd) || FALLBACK_LIMITS.maxTopupUsd;
    if (!Number.isFinite(amt) || amt < min || amt > max) {
      throw new Error(`Amount must be between $${min} and $${max}`);
    }
    return apiService.request('/credits/checkout', {
      method: 'POST',
      body: JSON.stringify({ amountUsd: amt }),
    });
  },

  /**
   * Notify all listeners that the credit balance may have changed
   * (deploy succeeded, checkout returned, etc.). Header chip + Billing
   * page both subscribe.
   */
  fireChangedEvent() {
    try {
      window.dispatchEvent(new CustomEvent(CREDIT_CHANGED_EVENT));
    } catch {
      // SSR / non-browser callers — no-op
    }
  },
};

export default Credits;
